import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CheckSquare,
  Clock,
  PhoneCall,
  RotateCw,
  Timer,
  Users
} from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Sidebar } from '../components/Sidebar'
import {
  buildOperationsDashboardMetrics,
  buildOperationsHomeItems,
  buildOperationsNextAction,
  buildOperationsRecoveryItems,
  type OperationsHomeItem,
  type OperationsDashboardCall,
  type OperationsRecoveryItem
} from '../lib/operationsDashboard'
import { hasInternalApi, internalApiGet } from '../lib/internalApi'
import { retryFailedCall } from '../lib/callRetry'
import { supabase } from '../lib/supabase'
import { useLivers } from '../lib/useLivers'

/**
 * Default landing page after sign-in.
 * Once a liver is picked from the sidebar, navigation goes to /liver/:id
 * which renders LiverDetailPage. Until then, this empty-state placeholder
 * shows.
 */
export function DashboardPage() {
  const qc = useQueryClient()
  const livers = useLivers()
  const calls = useQuery<OperationsDashboardCall[]>({
    queryKey: ['operations-dashboard', 'calls'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<{ calls: OperationsDashboardCall[] }>('/api/calls?limit=1000')
        return data.calls
      }

      const { data, error } = await supabase
        .from('call_records')
        .select('id,liver_id,called_at,duration_seconds,status,todos,next_follow_date,summary,error_message,updated_at,audio_storage_path,transcript')
        .order('called_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as OperationsDashboardCall[]
    }
  })
  const retryCall = useMutation({
    mutationFn: retryRecoveryItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations-dashboard', 'calls'] })
    }
  })
  const metrics = buildOperationsDashboardMetrics(calls.data ?? [], livers.data ?? [], new Date())
  const nextAction = buildOperationsNextAction(metrics)
  const homeItems = buildOperationsHomeItems(calls.data ?? [], livers.data ?? [], new Date(), {
    limit: 6
  })
  const recoveryItems = buildOperationsRecoveryItems(calls.data ?? [], livers.data ?? [], new Date(), {
    limit: 4,
    allowStuckRetry: hasInternalApi()
  })
  const loading = calls.isLoading || livers.isLoading
  const error = calls.error ?? livers.error

  return (
    <div className="min-h-full flex">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">ホーム</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                今日見るべき対応と、Recorder / Gateway の処理状態をまとめています。
              </p>
            </div>
            {loading && <p className="text-xs text-gray-400">集計中...</p>}
            {error && (
              <p className="text-xs text-red-600">
                集計の取得に失敗: {error instanceof Error ? error.message : String(error)}
              </p>
            )}
          </header>

          <section className={`rounded-2xl border px-4 py-4 sm:px-5 ${NEXT_ACTION_PANEL_CLASS[nextAction.tone]}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-70">次にやること</p>
                <h2 className="mt-1 text-lg font-semibold text-gray-950">{nextAction.title}</h2>
                <p className="mt-1 text-sm leading-6 text-gray-700">{nextAction.description}</p>
              </div>
              <Link
                to={nextActionHref(nextAction.primaryLabel)}
                className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-lg bg-gray-950 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                {nextAction.primaryLabel}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">今日の優先対応</h2>
                <p className="mt-1 text-xs text-gray-500">
                  failed / stuck / 期限超過 / 本日フォローを優先して並べています。
                </p>
              </div>
              <span className="w-fit rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                {homeItems.length}件
              </span>
            </div>
            {loading ? (
              <p className="text-sm text-gray-400">読み込み中...</p>
            ) : homeItems.length > 0 ? (
              <div className="grid gap-3 lg:grid-cols-2">
                {homeItems.map((item) => (
                  <HomeActionCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">急ぎの対応はありません</p>
                  <p className="mt-1 text-xs text-emerald-700">
                    ライバー詳細から直近の通話やLINE履歴を確認できます。
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">処理復旧</h2>
                <p className="mt-1 text-xs text-gray-500">
                  録音失敗、通信失敗、Gateway 停止で残った failed / stuck を確認します。
                </p>
              </div>
              <Link to="/reports" className="text-xs font-medium text-sky-700 hover:text-sky-800">
                Reportsで全件を見る
              </Link>
            </div>
            {loading ? (
              <p className="text-sm text-gray-400">読み込み中...</p>
            ) : recoveryItems.length > 0 ? (
              <div className="grid gap-3 lg:grid-cols-2">
                {recoveryItems.map((item) => (
                  <RecoveryCard
                    key={item.id}
                    item={item}
                    onRetry={() => retryCall.mutate(item)}
                    retrying={retryCall.isPending && retryCall.variables?.callId === item.callId}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                復旧が必要な通話はありません。
              </p>
            )}
            {retryCall.error && (
              <p className="mt-3 text-xs text-red-600">
                再処理に失敗: {retryCall.error instanceof Error ? retryCall.error.message : String(retryCall.error)}
              </p>
            )}
          </section>

          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-semibold text-gray-700">運用サマリー</h2>
            <p className="text-xs text-gray-400">詳細レポート画面は次フェーズで分離予定</p>
          </div>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              icon={<PhoneCall className="h-4 w-4" />}
              label="今月の通話数"
              value={`${metrics.monthlyCallCount}件`}
              tone="sky"
            />
            <MetricTile
              icon={<Timer className="h-4 w-4" />}
              label="今月の総通話時間"
              value={formatDuration(metrics.monthlyDurationSeconds)}
              tone="emerald"
            />
            <MetricTile
              icon={<AlertCircle className="h-4 w-4" />}
              label="failed"
              value={`${metrics.failedCallCount}件`}
              tone="red"
            />
            <MetricTile
              icon={<Clock className="h-4 w-4" />}
              label="stuck候補"
              value={`${metrics.stuckCallCount}件`}
              tone="amber"
            />
            <MetricTile
              icon={<CheckSquare className="h-4 w-4" />}
              label="未完了TODO"
              value={`${metrics.openTodoCount}件`}
              tone="violet"
            />
            <MetricTile
              icon={<AlertCircle className="h-4 w-4" />}
              label="期限超過TODO"
              value={`${metrics.overdueTodoCount}件`}
              tone="rose"
            />
            <MetricTile
              icon={<Users className="h-4 w-4" />}
              label="フォロー期限超過"
              value={`${metrics.overdueNextFollowCount}件`}
              tone="orange"
            />
            <MetricTile
              icon={<Users className="h-4 w-4" />}
              label="休眠候補"
              value={`${metrics.dormantLiverCount}件`}
              tone="slate"
            />
          </section>
        </div>
      </main>
    </div>
  )
}

function RecoveryCard({
  item,
  onRetry,
  retrying
}: {
  item: OperationsRecoveryItem
  onRetry: () => void
  retrying: boolean
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">{item.liverName}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {STATUS_LABEL[item.status]} / {item.updatedAt ? formatShortDate(item.updatedAt) : formatShortDate(item.calledAt)}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-medium ${item.retryable ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {item.retryable ? '再処理可' : '自動不可'}
        </span>
      </div>
      <p className="mt-2 text-xs leading-5 text-gray-600">{item.reason}</p>
      {item.errorMessage && <p className="mt-1 line-clamp-1 text-xs text-red-600">{item.errorMessage}</p>}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!item.retryable || retrying}
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          <RotateCw className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
          再処理
        </button>
        <Link to={`/liver/${item.liverId}`} className="text-xs font-medium text-gray-500 hover:text-gray-700">
          詳細を見る
        </Link>
      </div>
    </div>
  )
}

function HomeActionCard({ item }: { item: OperationsHomeItem }) {
  const content = (
    <div className="flex h-full items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 transition hover:border-gray-300 hover:bg-white">
      <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${HOME_TONE_CLASS[item.tone]}`}>
        {homeIcon(item.kind)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{item.title}</p>
            <p className="mt-0.5 truncate text-xs text-gray-500">{item.liverName}</p>
          </div>
          {item.date && <span className="shrink-0 text-[11px] text-gray-400">{formatShortDate(item.date)}</span>}
        </div>
        <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-600">{item.description}</p>
      </div>
      {item.liverId && <ArrowRight className="mt-2 h-4 w-4 shrink-0 text-gray-300" />}
    </div>
  )

  return item.liverId ? <Link to={`/liver/${item.liverId}`}>{content}</Link> : content
}

function MetricTile({
  icon,
  label,
  value,
  tone
}: {
  icon: ReactNode
  label: string
  value: string
  tone: MetricTone
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}>
          {icon}
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  )
}

type MetricTone = 'sky' | 'emerald' | 'red' | 'amber' | 'violet' | 'rose' | 'orange' | 'slate'

const TONE_CLASS: Record<MetricTone, string> = {
  sky: 'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  violet: 'bg-violet-50 text-violet-700',
  rose: 'bg-rose-50 text-rose-700',
  orange: 'bg-orange-50 text-orange-700',
  slate: 'bg-slate-100 text-slate-700'
}

const HOME_TONE_CLASS: Record<OperationsHomeItem['tone'], string> = {
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  rose: 'bg-rose-50 text-rose-700',
  sky: 'bg-sky-50 text-sky-700',
  slate: 'bg-slate-100 text-slate-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700'
}

const STATUS_LABEL: Record<OperationsDashboardCall['status'], string> = {
  pending: 'pending',
  recording: 'recording',
  transcribing: 'transcribing',
  awaiting_summary: 'awaiting_summary',
  summarizing: 'summarizing',
  complete: 'complete',
  failed: 'failed'
}

function retryRecoveryItem(item: OperationsRecoveryItem): Promise<void> {
  return retryFailedCall({
    id: item.callId,
    status: item.status,
    audio_storage_path: item.audioStoragePath,
    transcript: item.transcript
  })
}

function homeIcon(kind: OperationsHomeItem['kind']): ReactNode {
  switch (kind) {
    case 'failed_call':
      return <AlertCircle className="h-4 w-4" />
    case 'stuck_call':
      return <Clock className="h-4 w-4" />
    case 'overdue_todo':
      return <CheckSquare className="h-4 w-4" />
    case 'due_follow':
      return <Users className="h-4 w-4" />
    case 'dormant_liver':
      return <Users className="h-4 w-4" />
    case 'recent_call':
      return <PhoneCall className="h-4 w-4" />
  }
}

function formatShortDate(value: string): string {
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric'
  })
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}秒`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes < 60) return rest === 0 ? `${minutes}分` : `${minutes}分${rest}秒`
  const hours = Math.floor(minutes / 60)
  const minuteRest = minutes % 60
  return minuteRest === 0 ? `${hours}時間` : `${hours}時間${minuteRest}分`
}

const NEXT_ACTION_PANEL_CLASS: Record<OperationsHomeItem['tone'], string> = {
  red: 'border-red-200 bg-red-50',
  amber: 'border-amber-200 bg-amber-50',
  rose: 'border-rose-200 bg-rose-50',
  sky: 'border-sky-200 bg-sky-50',
  slate: 'border-slate-200 bg-slate-50',
  emerald: 'border-emerald-200 bg-emerald-50',
  violet: 'border-violet-200 bg-violet-50'
}

function nextActionHref(label: string): string {
  if (label.includes('TODO')) return '/tasks'
  if (label.includes('復旧') || label.includes('stuck')) return '/reports'
  if (label.includes('休眠') || label.includes('フォロー')) return '/livers'
  return '/livers'
}
